import { Button, ButtonGroup, Colors, Spinner, Tag } from "@blueprintjs/core";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import { IFGCustomInputProps, useFGContext } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import { ClearButton, TuteurSelectorDialog } from "../..";
import { TuteurApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { useAuth } from "../../../contexts";
import { useApiService } from "../../../hooks";
import { FGReadOnlyInput } from "../FGReadOnlyInput";

const InputContainer = styled.div`
  padding: 0.4rem 0 0 0.6rem;
`;

const StyledTag = styled(Tag)`
  & a {
    color: ${Colors.WHITE};
  }
`;

const NamesContainer = styled.div`
  > * + * {
    margin-left: 5px;
  }
`;

export interface ITuteurSelectorFieldProps extends Omit<IFGCustomInputProps, "children"> {
  parentTuteurIds?: number[];
  disabled?: boolean;
  allowMultipleSelection?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  idlieuFormation?: number;
}

export const TuteurSelectorField: React.FunctionComponent<ITuteurSelectorFieldProps> = ({
  name,
  parentTuteurIds,
  label = null,
  disabled = false,
  className,
  allowMultipleSelection = false,
  ref,
  idlieuFormation,
  ...fgCustomInputProps
}) => {
  const { hasPermission } = useAuth();
  const ctx = useFGContext();
  const api = useApiService(TuteurApi);

  const hasValue = React.useMemo(
    () =>
      (allowMultipleSelection && !isEmpty(ctx?.formik?.values?.[name])) ||
      (!allowMultipleSelection && !!ctx?.formik?.values?.[name]),
    [allowMultipleSelection, ctx?.formik?.values, name]
  );
  const [selectorDialogOpen, setSelectorDialogOpen] = React.useState(false);
  const [currentSelectedTuteurIds, setSelectedTuteurIds] = React.useState<number[]>([]);

  const { data: displayNames, isFetching: loadingDisplayNames } = useQuery(
    ["lieu-formation-display-names", currentSelectedTuteurIds],
    async () => api.tuteurGetDisplayNames({ request_body: currentSelectedTuteurIds })
  );

  const clearSelectedTuteur = React.useCallback(() => {
    ctx?.formik?.setFieldValue(name, allowMultipleSelection ? [] : null, true);
    setSelectedTuteurIds([]);
  }, [allowMultipleSelection, ctx?.formik, name]);

  React.useEffect(() => {
    if (ctx?.formik?.values?.[name]) {
      const value = ctx?.formik?.values?.[name];
      setSelectedTuteurIds(isArray(value) ? value : [value]);
    } else {
      setSelectedTuteurIds([]);
    }
  }, [ctx?.formik?.values, name]);

  const onTuteursSelected = React.useCallback(
    (selectedTuteurIds?: number[]) => {
      if (selectedTuteurIds?.length > 0) {
        ctx?.formik?.setFieldTouched(name);
        ctx?.formik?.setFieldValue(name, allowMultipleSelection ? selectedTuteurIds : selectedTuteurIds[0], true);
        setSelectedTuteurIds(selectedTuteurIds);
      }
    },
    [allowMultipleSelection, ctx?.formik, name]
  );

  const selectorProps = React.useMemo(
    () => ({
      excludedTuteurIds: parentTuteurIds,
      allowMultipleSelection,
      preSelectedIds: currentSelectedTuteurIds
    }),
    [allowMultipleSelection, parentTuteurIds, currentSelectedTuteurIds]
  );

  const openTuteurSelector = React.useCallback(() => {
    setSelectorDialogOpen(true);
  }, []);

  const getEditRoute = React.useCallback(
    (id: number) =>
      `#${ERoutes.tuteur}/${id}/detail/${hasPermission("REPRESENTANT", "RW", "DETAIL") ? "edit" : "view"}`,
    [hasPermission]
  );

  const removeTuteurId = React.useCallback(
    (tuteurId: number) => {
      const newTuteurIds = [...currentSelectedTuteurIds];
      newTuteurIds.splice(currentSelectedTuteurIds.indexOf(tuteurId), 1);
      ctx?.formik?.setFieldTouched(name);
      ctx?.formik?.setFieldValue(name, newTuteurIds, true);
      setSelectedTuteurIds(newTuteurIds);
    },
    [ctx?.formik, currentSelectedTuteurIds, name]
  );

  const getTuteurLink = React.useCallback(
    (id: number, name: string, tags: boolean) => {
      const link = hasPermission("LIEUFORMATION", "R", "DETAIL") ? (
        <a href={getEditRoute(id)} target="_blank" rel="noreferrer" tabIndex={-1} key={id}>
          {name}
        </a>
      ) : (
        name
      );
      return tags ? (
        <StyledTag key={id} onRemove={() => removeTuteurId(id)}>
          {link}
        </StyledTag>
      ) : (
        link
      );
    },
    [getEditRoute, hasPermission, removeTuteurId]
  );

  const onClosed = React.useCallback(
    (selectedTuteurIds?: number[]) => {
      onTuteursSelected(selectedTuteurIds);
      setSelectorDialogOpen(false);
    },
    [onTuteursSelected]
  );

  return (
    <>
      <FGReadOnlyInput
        name={name}
        label={label}
        leftElement={
          <InputContainer>
            {loadingDisplayNames ? (
              <Spinner size={16} />
            ) : (
              <NamesContainer>
                {displayNames.map(dn => getTuteurLink(dn.id, dn.text, allowMultipleSelection))}
              </NamesContainer>
            )}
          </InputContainer>
        }
        rightElement={
          <ButtonGroup>
            {hasValue && <ClearButton onClick={clearSelectedTuteur} tabIndex={-1} disabled={disabled} />}
            <Button icon="office" minimal={true} onClick={openTuteurSelector} disabled={disabled} tabIndex={-1} />
          </ButtonGroup>
        }
        {...fgCustomInputProps}
      />
      <TuteurSelectorDialog
        onClosed={onClosed}
        onTuteurSelected={onClosed}
        selectorDialogOpen={selectorDialogOpen}
        idlieuFormation={idlieuFormation}
        {...selectorProps}
      />
    </>
  );
};
